import jsPDF from 'jspdf';

export const PDF_PAGE_MARGIN = 14;
export const PDF_FOOTER_ZONE = 14;
export const PDF_RIGHT_HEADER_WIDTH = 56;
export const PDF_LOGO_MAX_WIDTH = 56;
export const PDF_LOGO_MAX_HEIGHT = 28;
export const PDF_BRAND_TO_TITLE_GAP = 4;

type LogoFormat = 'PNG' | 'JPEG';

export interface PdfLogoAsset {
  dataUrl: string;
  format: LogoFormat;
  widthPx: number;
  heightPx: number;
}

/** Keep PDF text Latin-1 safe (Helvetica built-in font). */
export const toPdfText = (value: string): string =>
  value
    .replace(/\u2192/g, '->')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u00B7/g, ' | ')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/[^\u0009\u000A\u000D\u0020-\u007E\u00A0-\u00FF]/g, '');

const loadLogoAssetFromDataUrl = (dataUrl: string): Promise<PdfLogoAsset | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        resolve(null);
        return;
      }
      resolve({
        dataUrl,
        format: dataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG',
        widthPx: img.naturalWidth,
        heightPx: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(blob);
  });

/** Unity mark for platform documents (subscription invoices and receipts). */
export const loadUnityPdfLogo = async (): Promise<PdfLogoAsset | null> => {
  try {
    const response = await fetch('/brand/symbol-on-light.png');
    if (!response.ok) return null;
    const dataUrl = await blobToDataUrl(await response.blob());
    return loadLogoAssetFromDataUrl(dataUrl);
  } catch {
    return null;
  }
};

export interface PdfBrandHeaderInput {
  churchName: string;
  branchName?: string | null;
  logo?: PdfLogoAsset | null;
  compact?: boolean;
}

export interface PdfBrandHeaderResult {
  leftBottomY: number;
  blockBottomY: number;
}

export const drawPdfBrandHeaderRow = (
  doc: jsPDF,
  pageWidth: number,
  topY: number,
  input: PdfBrandHeaderInput,
): PdfBrandHeaderResult => {
  const compact = input.compact ?? false;
  const leftX = PDF_PAGE_MARGIN;
  const rightX = pageWidth - PDF_PAGE_MARGIN;
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const leftColWidth = contentWidth - PDF_RIGHT_HEADER_WIDTH - 4;

  const churchFontSize = compact ? 10 : 14;
  const branchFontSize = compact ? 10 : 12;
  const churchLineHeight = compact ? 4.5 : 6;
  const branchLineHeight = compact ? 4.5 : 5.5;

  const maxLogoWidth = compact ? PDF_LOGO_MAX_WIDTH * 0.8 : PDF_LOGO_MAX_WIDTH;
  const maxLogoHeight = compact ? PDF_LOGO_MAX_HEIGHT * 0.8 : PDF_LOGO_MAX_HEIGHT;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(churchFontSize);
  doc.setTextColor(40, 40, 40);
  const churchBaselineY = topY + (compact ? 3 : 4);
  const churchLines = doc.splitTextToSize(toPdfText(input.churchName), leftColWidth);
  doc.text(churchLines, leftX, churchBaselineY);

  let leftBottomY = churchBaselineY + churchLines.length * churchLineHeight;

  const branchName = input.branchName?.trim();
  if (branchName) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(branchFontSize);
    doc.setTextColor(80, 80, 80);
    const branchLines = doc.splitTextToSize(toPdfText(branchName), leftColWidth);
    doc.text(branchLines, leftX, leftBottomY + 1);
    leftBottomY += branchLines.length * branchLineHeight + 1;
  }

  let rightBottomY = topY;
  if (input.logo) {
    const aspect = input.logo.widthPx / input.logo.heightPx;
    let widthMm = maxLogoWidth;
    let heightMm = widthMm / aspect;
    if (heightMm > maxLogoHeight) {
      heightMm = maxLogoHeight;
      widthMm = heightMm * aspect;
    }
    doc.addImage(input.logo.dataUrl, input.logo.format, rightX - widthMm, topY, widthMm, heightMm);
    rightBottomY = topY + heightMm;
  }

  return {
    leftBottomY,
    blockBottomY: Math.max(leftBottomY, rightBottomY),
  };
};

export const drawPdfPageFooters = (
  doc: jsPDF,
  pageWidth: number,
  pageHeight: number,
  churchName: string,
): void => {
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`${page} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(toPdfText(churchName), pageWidth - PDF_PAGE_MARGIN, pageHeight - 8, { align: 'right' });
  }
};
